import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Package, PawPrint, FileText, LifeBuoy, Heart, Clock, ArrowRight, ShoppingBag, TrendingUp } from "lucide-react";
import { FEATURES } from "@/lib/features";

const StatCard = ({ icon: I, label, value, to }) => (
  <Link to={to} data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`} className="border border-slate-200 rounded-md p-5 bg-white card-clinical block">
    <div className="flex items-center justify-between">
      <div className="w-9 h-9 rounded-md bg-blue-50 border border-blue-200 flex items-center justify-center">
        <I className="w-4 h-4 text-blue-700" strokeWidth={1.75}/>
      </div>
      <ArrowRight className="w-4 h-4 text-slate-300" strokeWidth={1.75}/>
    </div>
    <div className="mt-6 font-display text-3xl font-semibold text-slate-900">{value}</div>
    <div className="text-xs text-slate-500 mt-1">{label}</div>
  </Link>
);

export default function DashboardOverview() {
  const { user } = useAuth();
  const { affiliate } = useOutletContext() || {};
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard/overview").then((r) => setData(r.data)).catch(() => setData({ counts: {}, recent_orders: [] }));
  }, []);

  if (!data) return <div className="text-slate-400 font-mono text-xs uppercase tracking-widest">Loading dashboard…</div>;
  const c = data.counts || {};

  return (
    <div data-testid="dashboard-overview">
      <div className="mb-8">
        <div className="eyebrow mb-2">Dashboard</div>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight">
          Welcome back, {user?.name?.split(" ")[0]}.
        </h1>
        <p className="mt-1 text-slate-600 text-sm">Everything about your research account, in one place.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard icon={Package} label="Total Orders" value={c.orders ?? 0} to="/dashboard/orders" />
        <StatCard icon={ShoppingBag} label="Active" value={c.active_orders ?? 0} to="/dashboard/orders" />
        <StatCard icon={FileText} label="Invoices" value={c.invoices ?? 0} to="/dashboard/invoices" />
        {FEATURES.pets && (
          <StatCard icon={PawPrint} label="Pets" value={c.pets ?? 0} to="/dashboard/pets" />
        )}
        <StatCard icon={LifeBuoy} label="Open Tickets" value={c.tickets_open ?? 0} to="/dashboard/tickets" />
        <StatCard icon={Heart} label="Wishlist" value={c.wishlist ?? 0} to="/dashboard/wishlist" />
      </div>

      <div className="mt-10">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl font-semibold text-slate-900">Recent Orders</h2>
          <Link to="/dashboard/orders" className="text-sm text-blue-700 hover:text-blue-800">View all →</Link>
        </div>
        <div className="border border-slate-200 rounded-md bg-white overflow-hidden">
          {(!data.recent_orders || data.recent_orders.length === 0) ? (
            <div className="p-8 text-center text-sm text-slate-500">No orders yet.</div>
          ) : (
            <table className="w-full text-sm" data-testid="recent-orders-table">
              <thead className="bg-slate-50 border-b border-slate-200 font-mono text-[10px] uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="text-left px-5 py-3">Order</th>
                  <th className="text-left px-5 py-3">Status</th>
                  <th className="text-left px-5 py-3">Placed</th>
                  <th className="text-right px-5 py-3">Total</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_orders.map((o) => (
                  <tr key={o.order_id} className="border-b border-slate-100 last:border-b-0">
                    <td className="px-5 py-4 font-mono text-slate-900">{o.order_id}</td>
                    <td className="px-5 py-4">{o.status}</td>
                    <td className="px-5 py-4 text-slate-600">{new Date(o.placed_at).toLocaleDateString()}</td>
                    <td className="px-5 py-4 text-right font-mono">${Number(o.total).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {affiliate?.status === "none" && affiliate?.signup_enabled && (
        <div className="mt-8 border border-blue-200 bg-blue-50/60 rounded-md p-5 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="become-affiliate-cta">
          <TrendingUp className="w-6 h-6 text-blue-600" strokeWidth={1.75} />
          <div className="flex-1">
            <div className="font-semibold text-slate-900">Earn commission by referring new customers.</div>
            <p className="text-xs text-slate-600 mt-0.5">Apply to join our affiliate program — tiered rates up to 25%.</p>
          </div>
          <Link
            to="/affiliates/apply"
            data-testid="become-affiliate-link"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium whitespace-nowrap"
          >
            Apply now
          </Link>
        </div>
      )}
    </div>
  );
}